import { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";

// Editor de comisión de edificio (Bloque C Fase 4). Trabaja sobre las columnas EXISTENTES:
// 3 bandas variables (comision_1/2/3_*) + 1 fija (comision_fija_*). NO migra a tramos infinitos.
// Solo arma el objeto y delega el guardado (updateEdificio) al padre, pasando el OBJETO ENTERO
// del edificio (regla 10.54). Regla dura: tramos activos no pueden solaparse en fechas.
//
// Props: { show, edificio, saving, onClose, onSubmit(objEdificioCompleto) }

// Fecha string ISO ("YYYY-MM-DD", formato DB) ↔ Date local. Mismo patrón que FlujoCaja/MovimientoModal.
const parseFechaLocal = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-");
  return new Date(y, m - 1, d);
};
const formatFechaToISO = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MONEDAS = ["USD", "CLP", "PEN"];

// Estado de un tramo: "vacio" (slot libre → se limpia), "activo" (los 3 campos) o "parcial" (a medias → error).
const tieneValor = (v) => v !== "" && v !== null && v !== undefined;
const clasificar = (campoPrincipal, desde, hasta) => {
  const n = (tieneValor(campoPrincipal) ? 1 : 0) + (desde ? 1 : 0) + (hasta ? 1 : 0);
  if (n === 0) return "vacio";
  if (n === 3) return "activo";
  return "parcial";
};

export const EdificioComisionModal = ({ show, edificio, saving, onClose, onSubmit }) => {
  const init = edificio || {};

  const [v1, setV1] = useState({
    pct: init.comision_1_porcentaje ?? "",
    desde: init.comision_1_fechainicio || "",
    hasta: init.comision_1_fechafin || "",
  });
  const [v2, setV2] = useState({
    pct: init.comision_2_porcentaje ?? "",
    desde: init.comision_2_fechainicio || "",
    hasta: init.comision_2_fechafin || "",
  });
  const [v3, setV3] = useState({
    pct: init.comision_3_porcentaje ?? "",
    desde: init.comision_3_fechainicio || "",
    hasta: init.comision_3_fechafin || "",
  });
  const [fija, setFija] = useState({
    monto: init.comision_fija_monto ?? "",
    moneda: init.comision_fija_moneda || "USD",
    desde: init.comision_fija_inicio || "",
    hasta: init.comision_fija_fin || "",
  });

  const handleGuardar = () => {
    const tramos = [
      { label: "Variable 1", principal: v1.pct, desde: v1.desde, hasta: v1.hasta, esVarFalta: "% o fechas" },
      { label: "Variable 2", principal: v2.pct, desde: v2.desde, hasta: v2.hasta, esVarFalta: "% o fechas" },
      { label: "Variable 3", principal: v3.pct, desde: v3.desde, hasta: v3.hasta, esVarFalta: "% o fechas" },
      { label: "Fija", principal: fija.monto, desde: fija.desde, hasta: fija.hasta, esVarFalta: "monto o fechas" },
    ].map((t) => ({ ...t, estado: clasificar(t.principal, t.desde, t.hasta) }));

    // 1. Ningún tramo a medias.
    const parcial = tramos.find((t) => t.estado === "parcial");
    if (parcial) {
      toast.error(`Completá el tramo ${parcial.label} (faltan ${parcial.esVarFalta})`);
      return;
    }

    const activos = tramos.filter((t) => t.estado === "activo");

    // 2. desde <= hasta en cada activo (fechas ISO comparan lexicográficamente bien).
    const invertido = activos.find((t) => t.desde > t.hasta);
    if (invertido) {
      toast.error(`El tramo ${invertido.label} tiene fecha inicio posterior a fin`);
      return;
    }

    // 3. No superposición sobre la unión de tramos activos (variables + fija).
    for (let i = 0; i < activos.length; i += 1) {
      for (let j = i + 1; j < activos.length; j += 1) {
        const a = activos[i];
        const b = activos[j];
        if (a.desde <= b.hasta && b.desde <= a.hasta) {
          toast.error(`Los tramos ${a.label} y ${b.label} se superponen en fechas`);
          return;
        }
      }
    }

    // Armado: objeto ENTERO (regla 10.54). Tramos no-activos → null (se limpian).
    const v1Activo = clasificar(v1.pct, v1.desde, v1.hasta) === "activo";
    const v2Activo = clasificar(v2.pct, v2.desde, v2.hasta) === "activo";
    const v3Activo = clasificar(v3.pct, v3.desde, v3.hasta) === "activo";
    const fijaActiva = clasificar(fija.monto, fija.desde, fija.hasta) === "activo";

    const obj = {
      ...edificio,
      comision_1_porcentaje: v1Activo ? Number(v1.pct) : null,
      comision_1_fechainicio: v1Activo ? v1.desde : null,
      comision_1_fechafin: v1Activo ? v1.hasta : null,
      comision_2_porcentaje: v2Activo ? Number(v2.pct) : null,
      comision_2_fechainicio: v2Activo ? v2.desde : null,
      comision_2_fechafin: v2Activo ? v2.hasta : null,
      comision_3_porcentaje: v3Activo ? Number(v3.pct) : null,
      comision_3_fechainicio: v3Activo ? v3.desde : null,
      comision_3_fechafin: v3Activo ? v3.hasta : null,
      // Fija: si queda vacía, monto/inicio/fin → null. La moneda se re-pasa como esté (no rompe nada).
      comision_fija_monto: fijaActiva ? Number(fija.monto) : null,
      comision_fija_moneda: fija.moneda,
      comision_fija_inicio: fijaActiva ? fija.desde : null,
      comision_fija_fin: fijaActiva ? fija.hasta : null,
    };

    onSubmit(obj);
  };

  // Celda DatePicker reutilizable (string ISO ↔ Date).
  const fechaInput = (valor, onChange) => (
    <DatePicker
      selected={parseFechaLocal(valor)}
      onChange={(date) => onChange(formatFechaToISO(date) || "")}
      dateFormat="dd/MM/yyyy"
      placeholderText="—"
      isClearable
      className="form-control form-control-sm"
    />
  );

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Comisión — {edificio?.edificio?.toUpperCase()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ color: "#666", fontSize: "0.85rem", marginBottom: 12 }}>
          Una secuencia de tramos en el tiempo (variables y/o fija). No pueden solaparse: en cada fecha, una sola
          comisión activa. Se permiten huecos. Dejá un tramo vacío para liberar ese slot.
        </p>

        <table className="table table-sm table-bordered align-middle" style={{ fontSize: "0.85rem" }}>
          <thead className="table-dark text-center">
            <tr>
              <th style={{ width: "16%" }}>Tramo</th>
              <th style={{ width: "26%" }}>% / Monto</th>
              <th style={{ width: "29%" }}>Desde</th>
              <th style={{ width: "29%" }}>Hasta</th>
            </tr>
          </thead>
          <tbody>
            {/* Variable 1/2/3 */}
            {[
              { label: "Variable 1", t: v1, set: setV1 },
              { label: "Variable 2", t: v2, set: setV2 },
              { label: "Variable 3", t: v3, set: setV3 },
            ].map(({ label, t, set }) => (
              <tr key={label}>
                <td>
                  <strong>{label}</strong>
                </td>
                <td>
                  <div className="input-group input-group-sm">
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      className="form-control form-control-sm"
                      placeholder="%"
                      value={t.pct}
                      onChange={(e) => set((p) => ({ ...p, pct: e.target.value }))}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                </td>
                <td>{fechaInput(t.desde, (val) => set((p) => ({ ...p, desde: val })))}</td>
                <td>{fechaInput(t.hasta, (val) => set((p) => ({ ...p, hasta: val })))}</td>
              </tr>
            ))}

            {/* Fija */}
            <tr>
              <td>
                <strong>Fija</strong>
              </td>
              <td>
                <div className="d-flex gap-1">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control form-control-sm"
                    placeholder="Monto"
                    value={fija.monto}
                    onChange={(e) => setFija((p) => ({ ...p, monto: e.target.value }))}
                  />
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 90 }}
                    value={fija.moneda}
                    onChange={(e) => setFija((p) => ({ ...p, moneda: e.target.value }))}
                  >
                    {MONEDAS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </td>
              <td>{fechaInput(fija.desde, (val) => setFija((p) => ({ ...p, desde: val })))}</td>
              <td>{fechaInput(fija.hasta, (val) => setFija((p) => ({ ...p, hasta: val })))}</td>
            </tr>
          </tbody>
        </table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
